
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";
import { useState } from "react";

const BestRestorationContractorsPage = () => {
  const serviceType = "Restoration";
  const pageTitle = `Best ${serviceType} Contractors in Oklahoma City, OK`;
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'restoration'],
    queryFn: async () => {
      console.log('Fetching restoration contractors...');
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Restoration')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      console.log(`Found ${data?.length || 0} restoration contractors`);
      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "Restoration", href: "/best-restoration-contractors-okc" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established restoration contractors in Oklahoma City for water damage, fire damage, and storm damage restoration. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-restoration-contractors-okc",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established restoration contractors in Oklahoma City for water damage, fire damage, and storm damage restoration. Free quotes, trusted professionals."
        canonicalUrl="/best-restoration-contractors-okc"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable restoration contractors for all your water, fire, and storm damage restoration needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our Restoration Contractors</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Emergency Response</h3>
                <p>Our restoration contractors offer 24/7 emergency services to minimize damage and begin the recovery process immediately.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Certified Professionals</h3>
                <p>Work with IICRC certified technicians who follow industry standards and best practices for restoration.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Insurance Coordination</h3>
                <p>Our network of professionals can work directly with your insurance company to streamline the claims process.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our Restoration Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Water Damage Restoration</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Emergency water extraction</li>
                  <li>Structural drying and dehumidification</li>
                  <li>Mold prevention and remediation</li>
                  <li>Sewage cleanup and sanitization</li>
                  <li>Flood damage restoration</li>
                  <li>Content cleaning and restoration</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Fire & Smoke Damage</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Smoke and soot removal</li>
                  <li>Structural cleaning and restoration</li>
                  <li>Odor elimination</li>
                  <li>Content pack-out and cleaning</li>
                  <li>Reconstruction services</li>
                  <li>Emergency board-up and tarping</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <div id="quote-request">
          <QuoteRequestForm serviceName={serviceType} />
        </div>
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Restoration Contractors in Oklahoma City</h2>
          
          {isLoading ? (
            <LoadingState message="Loading restoration contractors..." />
          ) : contractors && contractors.length > 0 ? (
            <>
              <p className="mb-4 text-muted-foreground">
                Found {contractors.length} restoration contractors serving Oklahoma City.
              </p>
              <ContractorsList
                contractors={contractors}
                isLoading={isLoading}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onServiceTypeChange={() => {}}
                onLocationChange={() => {}}
                selectedServiceType="Restoration"
              />
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of restoration contractors in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BestRestorationContractorsPage;
